import { SCENE } from "../core/keys"
import { postWaveObserverMessage } from "../core/wave-observer"
import { SCENE_TUNING } from "../data/constants"
import { ENEMY_DEFINITIONS } from "../data/enemies"
import type { Character, Enemy, GameData, GameSprite } from "../types"
import type { WaveTimeline } from "./wave-state"

const OBSERVER_STATE_INTERVAL_MS = 250

interface ObserverBridgeOptions {
  scene: import("phaser").Scene
  waveObserver: { enabled: boolean }
  getGameData: () => GameData
  getWaveNumber: () => number
  getCharacter: () => Character
  waveTimeline: WaveTimeline
  enemyGroup: import("phaser").Physics.Arcade.Group
  getEnemyPopulationCap: () => number
  setRunStage: (next: GameData["state"]) => void
}

interface EnemySpawnStats {
  enemyType: string
  hp: number
  damage: number
  points: number
}

export class ObserverBridge {
  private readonly options: ObserverBridgeOptions
  private lastStatePostAt = 0

  constructor(options: ObserverBridgeOptions) {
    this.options = options
  }

  public get enabled(): boolean {
    return this.options.waveObserver.enabled
  }

  public postReady(): void {
    if (!this.enabled) return
    const { getWaveNumber, getCharacter, waveTimeline } = this.options
    const sector = waveTimeline.getCurrentSector()
    const waveDuration = waveTimeline.getCurrentWaveDuration()

    postWaveObserverMessage("ready", {
      wave: getWaveNumber(),
      heroId: getCharacter().id,
      heroName: getCharacter().name,
      sectorId: sector.id,
      sectorName: sector.name,
      durationMs: waveDuration,
      targetDistance: Math.max(
        1,
        Math.round((waveDuration / 1000) * SCENE_TUNING.pushDistanceRateBase),
      ),
      bossWave: waveTimeline.isBossWave(),
    })
  }

  private getEnemySpawnStats(
    enemy: Enemy | GameSprite,
  ): EnemySpawnStats | null {
    const ed = "entityData" in enemy ? enemy.entityData : undefined
    if (ed && ed.kind !== "enemy") return null

    return {
      enemyType: ed?.type ?? ("type" in enemy ? enemy.type : "submarine"),
      hp: ed?.hp ?? ("hp" in enemy ? enemy.hp : 0),
      damage: ed?.damage ?? ("damage" in enemy ? enemy.damage : 0),
      points: ed?.points ?? ("points" in enemy ? enemy.points : 0),
    }
  }

  public observeSpawn(
    enemy: Enemy | GameSprite,
    source: "boss" | "encounter" | "batch" | "fallback",
  ): void {
    if (!this.enabled) return
    const { getWaveNumber, waveTimeline, enemyGroup } = this.options
    const stats = this.getEnemySpawnStats(enemy)
    if (!stats) return
    const { enemyType, hp, damage, points } = stats
    const def = ENEMY_DEFINITIONS[enemyType as keyof typeof ENEMY_DEFINITIONS]

    postWaveObserverMessage("spawn", {
      wave: getWaveNumber(),
      source,
      enemyType,
      x: Math.round(enemy.x),
      y: Math.round(enemy.y),
      hp,
      damage,
      points,
      threatTier: def?.threatTier ?? 0,
      activeEnemies: enemyGroup.countActive(true),
      waveProgress: waveTimeline.getWaveProgress(),
    })
  }

  public postState(force = false): void {
    if (!this.enabled) return

    const now = this.options.scene.time.now
    if (!force && now - this.lastStatePostAt < OBSERVER_STATE_INTERVAL_MS)
      return
    this.lastStatePostAt = now

    const {
      getWaveNumber,
      waveTimeline,
      enemyGroup,
      getGameData,
      getEnemyPopulationCap,
    } = this.options
    const gameData = getGameData()

    const activeByType: Record<string, number> = {}
    for (const sprite of enemyGroup.getChildren() as GameSprite[]) {
      if (!sprite.active) continue
      const ed = sprite.entityData
      if (!ed || ed.kind !== "enemy") continue
      activeByType[ed.type] = (activeByType[ed.type] ?? 0) + 1
    }

    postWaveObserverMessage("state", {
      wave: getWaveNumber(),
      sectorId: waveTimeline.getCurrentSector().id,
      sectorName: waveTimeline.getCurrentSector().name,
      bossWave: waveTimeline.isBossWave(),
      progress: waveTimeline.getWaveProgress(),
      pushDistance: gameData.pushDistance,
      pushTargetDistance: gameData.pushTargetDistance,
      runDistance: gameData.runDistance,
      elapsedMs: waveTimeline.getElapsedTime(),
      durationMs: waveTimeline.getCurrentWaveDuration(),
      remainingMs: waveTimeline.getRemainingTime(),
      spawnIntervalMs: waveTimeline.getSpawnIntervalMs(),
      activeEnemies: enemyGroup.countActive(true),
      enemyCap: getEnemyPopulationCap(),
      activeByType,
      score: gameData.score,
      coins: gameData.coins,
      playerHp: gameData.player.hp,
      maxHp: gameData.player.maxHp,
    })
  }

  public restart(reason: "wave-complete" | "victory" | "defeat"): void {
    if (!this.enabled) return

    const { scene, getGameData, getWaveNumber, setRunStage } = this.options
    const gameData = getGameData()

    setRunStage("playing")
    scene.physics.pause()
    if (scene.scene.isActive(SCENE.HUD)) {
      scene.scene.stop(SCENE.HUD)
    }

    postWaveObserverMessage("loop", {
      wave: getWaveNumber(),
      reason,
      score: gameData.score,
      coins: gameData.coins,
    })

    scene.time.delayedCall(900, () => {
      if (scene.scene.isActive(SCENE.WAVE)) {
        scene.scene.restart({ wave: getWaveNumber() })
      }
    })
  }
}
